import { ACTION } from "actions/paymentIntentActions";
import { PrivacyLink, TermsLink } from "components/ezpay/common/terms-privacy-links/terms-privacy-links";
import { WarningInformationBox } from "components/ezpay/common/warning-information-box/warning-information-box";
import FundingOption, {
  CardNetworkShortCode,
  CardNetworkType,
  getCardNetworkByShortCode,
  getCardNetworkFullNames,
  getSupportedCreditCardNetworks,
} from "constants/FundingOption";
import {
  COMMON_NAMESPACE,
  CREDIT_DEBIT_MODAL,
  SELECT_PAYMENT_METHOD_MODAL_NAMESPACE,
} from "constants/i18n-namespaces";
import { useFormik } from "formik";
import useWallet from "hooks/useWallet";
import { PLATFORM_NAMESPACE } from "kubra-labels";
import type { AnalyticsEvent } from "kubra-ui-lib-mfe";
import { AnalyticsEventType, useTracking } from "kubra-ui-lib-mfe";
import {
  Button,
  CardInput,
  InputField,
  TextButton,
  Toggle,
  Tooltip,
  TooltipEnums,
} from "kubra-ux-forge";
import moment from "moment";
import { ChangeEvent, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getBiller } from "selectors";
import { getIsMobile } from "selectors/appState";
import { getPayableSession } from "selectors/payableSession";
import * as Yup from "yup";
import { MethodComponentsOptions } from "../../payable-dashboard/payable-dashboard";
import { MAXIMUM_WALLET_ITEMS } from "../select-payment-method-modal";

export interface IPayableCreditDebitCard {
  cardHolderName: string;
  cardNo: string;
  cardType: string;
  cardExpiry: string;
  securityCode: string;
  zipCode: string;
  saveToWallet: boolean;
}

export const PayableCreditDebitCardInitialState: IPayableCreditDebitCard = {
  cardHolderName: "",
  cardNo: "",
  cardExpiry: "",
  securityCode: "",
  zipCode: "",
  saveToWallet: false,
  cardType: "",
};

export function isExpiryCardDateValid(cardExpiry: string | null | undefined) {
  const today = moment().startOf("day").toDate();
  if (cardExpiry === null || cardExpiry === undefined || cardExpiry === "") {
    return false;
  }

  const cardExpiryTokens = cardExpiry.split("/");

  const cardExpMonthIndex = +cardExpiryTokens[0] - 1;
  const cardExpYear = 2000 + +cardExpiryTokens[1];

  if (cardExpMonthIndex < 0 || cardExpMonthIndex > 11) {
    return false;
  }

  const expDate = new Date(cardExpYear, cardExpMonthIndex, 1, 0, 0, 0, 0);
  const currDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    1,
    0,
    0,
    0,
    0,
  );

  return expDate >= currDate;
}

export interface IPayableCreditDebitModalProps {
  closeModalCallBack: () => void;
  flowCallback: (method: MethodComponentsOptions) => void;
  showManagePaymentMethodsModal: () => void;
}

export type PayableCreditCardType =
  | "Visa"
  | "Mastercard"
  | "Discover"
  | "Amex"
  | "Diners";

export const PayableCreditDebitModal = ({
  closeModalCallBack,
  flowCallback,
  showManagePaymentMethodsModal,
}: IPayableCreditDebitModalProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(CREDIT_DEBIT_MODAL);
  const { trackEvent } = useTracking<Partial<AnalyticsEvent>>();
  const payableSession = useSelector(getPayableSession);
  const isMobile = useSelector(getIsMobile);

  const { getWalletItems } = useWallet(true);
  const walletItems = getWalletItems();

  const saveToWalletAllowed = walletItems.length < MAXIMUM_WALLET_ITEMS;

  const handleButtonCancel = () => {
    flowCallback("SelectPaymentMethodModal");
  };

  const isCardTypeSupported = (
    cardType: keyof typeof CardNetworkType,
  ): boolean => {
    const cardCode = CardNetworkShortCode[cardType];
    return payableSession?.methodOfPayments?.includes(cardCode);
  };

  const getSupportedCardErrorMessage = (
    cardType: keyof typeof CardNetworkType,
  ): string => {
    const unSupportedcardFullName = getCardNetworkFullNames(cardType);
    const supportedCreditCardNetworks = getSupportedCreditCardNetworks(
      payableSession?.methodOfPayments,
    );
    const supportedCards = supportedCreditCardNetworks?.map((method) =>
      getCardNetworkFullNames(getCardNetworkByShortCode(method)),
    );

    if (supportedCards?.length > 1) {
      const lastSupportedCard = supportedCards.pop();
      const allSupportedCardsExceptLast = supportedCards.join(", ");
      return t("card-not-supported-error", {
        unSupportedcardFullName: unSupportedcardFullName,
        supportedCards: allSupportedCardsExceptLast,
        lastSupportedCard,
      });
    }
    return t("card-not-supported-error-single", {
      unSupportedcardFullName: unSupportedcardFullName,
      supportedCard: supportedCards?.[0],
    });
  };

  const formik = useFormik({
    initialValues: { ...PayableCreditDebitCardInitialState },
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: Yup.object({
      cardHolderName: Yup.string()
        .required(t("cardholder-name-required"))
        .matches(/^[a-zA-Z\s]+$/, t("account-owner-name-required")),
      cardNo: Yup.string()
        .required(t("card-number-invalid"))
        .max(23, t("card-number-invalid"))
        .min(13, t("card-number-invalid"))
        .test(
          "cardNo",
          t("card-number-invalid"),
          function (value, { createError }) {
            const cardType = validateCreditCard(value);
            if (cardType !== null && isCardTypeSupported(cardType)) {
              return true;
            } else {
              const errorMessage = cardType
                ? getSupportedCardErrorMessage(cardType)
                : t("card-number-invalid");
              return createError({
                message: errorMessage,
              });
            }
          },
        ),
      cardExpiry: Yup.string()
        .required(t("expiration-date-required"))
        .max(5, t("expiration-date-invalid"))
        .min(5, t("expiration-date-invalid"))
        .test("cardExpiry", t("expiration-date-invalid"), function (value) {
          return isExpiryCardDateValid(value);
        }),
      securityCode: Yup.string()
        .required(t("cvc-invalid"))
        .matches(/^[0-9\b]+$/, t("cvc-invalid"))
        .max(4, t("cvc-invalid"))
        .min(3, t("cvc-invalid")),
      zipCode: Yup.string()
        .required(t("zip-code-invalid"))
        .matches(
          /^(?:\d{5}|^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\s?\d[ABCEGHJ-NPRSTV-Z]\d)$/i,
          t("zip-code-invalid"),
        )
        .max(7, t("zip-code-invalid"))
        .min(5, t("zip-code-invalid")),
      saveToWallet: Yup.bool(),

      cardType: Yup.string(),
    }),

    onSubmit: (values: IPayableCreditDebitCard, actions: any) => {
      actions.setSubmitting(true);

      const cardType = validateCreditCard(values.cardNo);

      if (cardType === null) {
        setFieldError("cardNo", t("card-number-invalid"));
        setSubmitting(false);
        return;
      }

      if (!isExpiryCardDateValid(values.cardExpiry)) {
        setFieldError("cardExpiry", t("expiration-date-invalid"));
        setSubmitting(false);
        return;
      }

      if (cardType === "Amex" && values.securityCode.length !== 4) {
        setFieldError("securityCode", t("cvc-invalid"));
        setSubmitting(false);
        return;
      }
      if (cardType !== "Amex" && values.securityCode.length !== 3) {
        setFieldError("securityCode", t("cvc-invalid"));
        setSubmitting(false);
        return;
      }
      values.cardType = cardType;
      dispatch({ type: ACTION.ADD_BANK_ACCOUNT, payload: null });
      dispatch({ type: ACTION.ADD_WALLET, payload: null });
      const payload = {
        ...values,
        firstName: values.cardHolderName,
        lastName: "",
      };
      dispatch({ type: ACTION.ADD_CREDIT_DEBIT_CARD, payload });
      dispatch({
        type: ACTION.ADD_PAYMENT_METHOD,
        payload: FundingOption.Card,
      });
      actions.resetForm();
      setSubmitting(false);
      trackEvent({
        type: AnalyticsEventType.customEvent,
        event: "card_created",
      });
      closeModalCallBack();
    },
  });

  const {
    values,
    touched,
    errors,
    isSubmitting,
    setFieldValue,
    setSubmitting,
    handleBlur,
    handleSubmit,
    setFieldError,
  } = formik;

  const cardHolderNameError =
    touched.cardHolderName ? errors.cardHolderName : undefined;
  const cardNoError = touched.cardNo ? errors.cardNo : undefined;
  const cardExpiryError = touched.cardExpiry ? errors.cardExpiry : undefined;
  const securityCodeError =
    touched.securityCode ? errors.securityCode : undefined;
  const zipCodeError = touched.zipCode ? errors.zipCode : undefined;

  const cardHolderNameErrorId = "payable-credit-debit-cardholder-name-error";
  const cardNoErrorId = "payable-credit-debit-card-number-error";
  const cardExpiryErrorId = "payable-credit-debit-card-expiry-error";
  const securityCodeErrorId = "payable-credit-debit-security-code-error";
  const zipCodeErrorId = "payable-credit-debit-zip-code-error";

  useEffect(() => {
    const errorIdMap: Array<[string, string, string | undefined]> = [
      [
        "input-cardholder-name-error-message",
        cardHolderNameErrorId,
        cardHolderNameError,
      ],
      ["input-card-error-message", cardNoErrorId, cardNoError],
      ["input-card-expiry-error-message", cardExpiryErrorId, cardExpiryError],
      [
        "input-security-code-error-message",
        securityCodeErrorId,
        securityCodeError,
      ],
      ["input-zip-code-error-message", zipCodeErrorId, zipCodeError],
    ];

    errorIdMap.forEach(([dataCy, id, errorText]) => {
      if (!errorText) {
        return;
      }

      const errorElement = document.querySelector(`[data-cy='${dataCy}']`);
      if (errorElement) {
        errorElement.setAttribute("id", id);
      }
    });
  }, [
    cardHolderNameError,
    cardNoError,
    cardExpiryError,
    securityCodeError,
    zipCodeError,
  ]);

  const handleCvcChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const regex = /^[0-9\b]+$/;
    if (
      event.currentTarget.value === "" ||
      regex.test(event.currentTarget.value)
    ) {
      await setFieldValue("securityCode", event.currentTarget.value);
    }
  };

  const handleCardZipChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const regex = /^[a-zA-Z0-9\s]+$/;
    if (
      event.currentTarget.value === "" ||
      regex.test(event.currentTarget.value)
    ) {
      await setFieldValue("zipCode", event.currentTarget.value);
    }
  };

  const handleCardExpiryChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const inputDate = event.currentTarget.value;
    let formattedInput = inputDate.replace(/\D/g, "").slice(0, 4);
    if (formattedInput.length > 2) {
      const month = formattedInput.slice(0, 2);
      const year = formattedInput.slice(2);
      if (Number.parseInt(month) > 12) {
        formattedInput = "12";
      } else {
        formattedInput = `${month}/${year}`;
      }
    }

    await setFieldValue("cardExpiry", formattedInput);
  };

  const handleToggleChange = () => {
    setFieldValue("saveToWallet", !values.saveToWallet);
  };

  const handleCardInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    void (async () => {
      const newcardNumber = event.currentTarget.value;
      await setFieldValue("cardNo", newcardNumber);
    })();
  };

  const handleCardHolderNameChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const regex = /^[a-zA-Z\s]+$/;
    if (
      event.currentTarget.value === "" ||
      regex.test(event.currentTarget.value)
    ) {
      await setFieldValue("cardHolderName", event.currentTarget.value);
    }
  };

  const validateCreditCard = (
    cardNumber: string,
  ): PayableCreditCardType | null => {
    const cleanedNumber = cardNumber.replace(/\D/g, "");

    const cardTypes: { type: PayableCreditCardType; regex: RegExp }[] = [
      { type: "Visa", regex: /^((4\d{12})|(4\d{15}))$/ },
      {
        type: "Mastercard",
        regex:
          /^((5[1-5]\d{2})|(2((22[1-9])|(2[3-9]\d)|([3-6]\d{2})|(7[01]\d)|(720))))\d{12}$/,
      },
      { type: "Amex", regex: /^(3[47]\d{13})$/ },
      {
        type: "Discover",
        regex: /^(6011\d{12}|65\d{14}|6[2-4]\d{14}|3[05-9]\d{14}|36\d{12})$/,
      },
      { type: "Diners", regex: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/ },
    ];

    for (const { type, regex } of cardTypes) {
      if (regex.test(cleanedNumber) && mod10Check(cleanedNumber)) {
        return type;
      }
    }

    return null; // Invalid card number
  };

  const mod10Check = (cardNumber: string): boolean => {
    let sum = 0;
    let isDouble = false;

    // Iterate over each digit from right to left
    for (let i = cardNumber.length - 1; i >= 0; i--) {
      let digit = Number.parseInt(cardNumber.charAt(i), 10);

      if (isDouble) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }

      sum += digit;
      isDouble = !isDouble;
    }
    return sum % 10 === 0;
  };

  const handleManagePaymentMethods = () => {
    closeModalCallBack();
    showManagePaymentMethodsModal();
  };

  const biller = useSelector(getBiller);

  const saveCardContainerClassName = !saveToWalletAllowed
    ? "save-card-container-disabled"
    : values.saveToWallet
      ? "save-card-container-enabled"
      : "save-card-container";

  return (
    <form onSubmit={handleSubmit} autoComplete="off">
      <div className="credit-debit-container">
        <div className="first-name-last-name-container">
          <div className="first-name-last-name-textbox">
            <InputField
              name="cardHolderName"
              id="cardHolderName"
              aria-required="true"
              errorText={cardHolderNameError}
              hasError={
                touched.cardHolderName && errors.cardHolderName != undefined
              }
              aria-describedby={
                cardHolderNameError ? cardHolderNameErrorId : undefined
              }
              value={values.cardHolderName}
              onChange={handleCardHolderNameChange}
              onBlur={handleBlur}
              helperText=""
              label={`${t("cardholder-name-label")}`}
              dataCy="input-cardholder-name"
              aria-invalid={touched.cardHolderName && !!errors.cardHolderName}
            />
          </div>
        </div>
        <div className="credit-debit-number-container">
          <CardInput
            name="cardNo"
            aria-required="true"
            id="cardNo"
            defaultValue={values.cardNo}
            errorText={cardNoError}
            hasError={touched.cardNo && errors.cardNo != undefined}
            aria-describedby={cardNoError ? cardNoErrorId : undefined}
            helperText=""
            label={`${t("card-number-label")}`}
            onChange={handleCardInputChange}
            onBlur={handleBlur}
            /* @ts-ignore */
            dataCy="input-card"
            aria-invalid={touched.cardNo && !!errors.cardNo}
          />
        </div>

        <div className="expiry-cvc-zip-container">
          <div className="expiry-cvc-zip-textbox">
            <div className="label-container">
              <label htmlFor="cardExpiry" className="label-text">
                {t("expiration-date-label")}
              </label>
            </div>
            <InputField
              id="cardExpiry"
              name="cardExpiry"
              aria-required="true"
              errorText={cardExpiryError}
              hasError={touched.cardExpiry && errors.cardExpiry != undefined}
              aria-describedby={
                cardExpiryError ? cardExpiryErrorId : undefined
              }
              helperText=""
              label={""}
              placeholder={`${t("expiration-date-placeholder")}`}
              maxLength="5"
              value={values.cardExpiry}
              onChange={handleCardExpiryChange}
              onBlur={handleBlur}
              dataCy="input-card-expiry"
              aria-invalid={touched.cardExpiry && !!errors.cardExpiry}
            />
          </div>

          <div className="expiry-cvc-zip-textbox">
            <div className="label-container">
              <label htmlFor="securityCode" className="label-text">
                {t("security-code-label")}
              </label>
              <Tooltip
                content={`${t("security-code-tooltip")}`}
                variant="rich"
                /* @ts-ignore */
                dataCy="tooltip-security-code"
              />
            </div>
            <InputField
              id="securityCode"
              name="securityCode"
              aria-required="true"
              hasShowHideButton
              errorText={securityCodeError}
              hasError={
                touched.securityCode && errors.securityCode != undefined
              }
              aria-describedby={
                securityCodeError ? securityCodeErrorId : undefined
              }
              helperText=""
              label=""
              maxLength="4"
              value={values.securityCode}
              onChange={handleCvcChange}
              onBlur={handleBlur}
              dataCy="input-security-code"
              aria-invalid={touched.securityCode && !!errors.securityCode}
            />
          </div>
          <div className="expiry-cvc-zip-textbox">
            <div className="label-container">
              <label htmlFor="zipCode" className="label-text">
                {t("zip-code-label")}
              </label>
              <Tooltip
                content={`${t("zip-code-tooltip")}`}
                direction={
                  isMobile ? TooltipEnums.TopRight : TooltipEnums.TopLeft
                }
                variant="rich"
                /* @ts-ignore */
                dataCy="tooltip-zip-code"
              />
            </div>
            <InputField
              id="zipCode"
              name="zipCode"
              aria-required="true"
              errorText={zipCodeError}
              helperText=""
              label=""
              hasError={touched.zipCode && errors.zipCode != undefined}
              aria-describedby={zipCodeError ? zipCodeErrorId : undefined}
              maxLength="7"
              value={values.zipCode}
              onChange={handleCardZipChange}
              onBlur={handleBlur}
              dataCy="input-zip-code"
              aria-invalid={touched.zipCode && !!errors.zipCode}
            />
          </div>
        </div>
        {payableSession?.payable?.category?.toLowerCase() === "account" ? (
          <div className="save-card-inputs-container">
            <div className={saveCardContainerClassName}>
              <div className="save-card-title-toggle-container">
                <span className="save-card-title">
                  {t("save-card-toggle-label")}
                </span>
                <Toggle
                  label={t("save-card-toggle-label")}
                  labelPosition="hidden"
                  onChange={handleToggleChange}
                  isCheckedDefault={values.saveToWallet}
                  dataCy="toggle-save-to-wallet"
                  data-testid="toggle-save-to-wallet"
                  disabled={!saveToWalletAllowed}
                />
              </div>
              {saveToWalletAllowed && (
                <>
                  <div
                    className={
                      values.saveToWallet
                        ? "save-card-line-separator-enabled"
                        : "save-card-line-separator"
                    }
                  />

                  <div className="save-card-text">
                    <Trans
                      i18nKey="save-card-credential"
                      ns={CREDIT_DEBIT_MODAL}
                      values={{
                        billerName: biller.billerName,
                        lastFourDigits:
                          values.cardNo && !errors.cardNo
                            ? values.cardNo.substring(values.cardNo.length - 4)
                            : "_____",
                      }}
                      components={[
                        <TermsLink key="terms" />,
                        <PrivacyLink key="privacy" />,
                      ]}
                    />
                  </div>
                </>
              )}
            </div>
            {!saveToWalletAllowed && (
              <WarningInformationBox>
                <Trans
                  i18nKey="max-payment-methods-message"
                  ns={SELECT_PAYMENT_METHOD_MODAL_NAMESPACE}
                  components={{
                    removeAction: (
                      <TextButton
                        onClick={handleManagePaymentMethods}
                        hoverStyle="underline"
                      >
                        {t("remove-payment-method")}
                      </TextButton>
                    ),
                  }}
                />
              </WarningInformationBox>
            )}
          </div>
        ) : null}
        <div className="buttons-row">
          <div className="button-item">
            <TextButton
              type="button"
              onClick={handleButtonCancel}
              dataCy="button-cancel"
            >
              {t("cancelLabel", { ns: PLATFORM_NAMESPACE })}
            </TextButton>
          </div>
          <div className="button-item">
            <Button
              disabled={isSubmitting}
              type="submit"
              onClick={() => null}
              dataCy="button-done"
            >
              {t("done", { ns: COMMON_NAMESPACE })}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};
